<template>
  <header class="site-header" :class="{ 'scrolled': scrolled }">
    <div class="header-container">
      <div class="header-left">
        <!-- Logo -->
        <router-link to="/home" class="logo-link">
          <div class="logo-icon">
            <span class="logo-character">晋</span>
            <div class="logo-hover-effect"></div>
            <span class="logo-character overlay">晋</span>
          </div>
          <div class="logo-text">
            晋韵寻踪
            <span class="underline-effect"></span>
          </div>
        </router-link>
        
        <div class="divider"></div>
        
        <!-- 桌面导航 -->
        <nav class="desktop-nav">
          <router-link 
            v-for="item in navItems" 
            :key="item.path" 
            :to="item.path"
            class="nav-item"
            :class="{ 'active': isActive(item.path) }"
          >
            <div class="nav-icon">
              <component :is="item.icon" class="icon" />
            </div>
            <span class="nav-text">{{ item.label }}</span>
            <span class="nav-indicator"></span>
          </router-link>
        </nav>
      </div>
      
      <div class="header-right">
        <SeasonSwitcher class="season-switcher" />
        
        <button 
          class="mobile-menu-button"
          @click="isMenuOpen = !isMenuOpen"
          aria-label="菜单"
        >
          <MenuIcon v-if="!isMenuOpen" class="menu-icon" />
          <XIcon v-else class="menu-icon" />
        </button>
      </div>
      <router-link to="/admin" class="admin">
        管理
      </router-link>
    </div>

    <!-- 移动端菜单 -->
    <Transition name="fade">
      <div 
        v-if="isMenuOpen"
        class="mobile-menu-overlay"
        @click="isMenuOpen = false"
      >
        <Transition name="slide">
          <div 
            v-if="isMenuOpen"
            class="mobile-menu"
            @click.stop
          >
            <div class="menu-header">
              <h3 class="menu-title">导航菜单</h3>
              <button 
                @click="isMenuOpen = false"
                class="close-button"
                aria-label="关闭菜单"
              >
                <XIcon class="close-icon" />
              </button>
            </div>
            
            <div class="mobile-season">
              <p class="season-title">选择季节</p>
              <SeasonSwitcher />
            </div>
            
            <nav class="mobile-nav">
              <router-link 
                v-for="item in navItems" 
                :key="item.path" 
                :to="item.path"
                class="mobile-nav-item"
                :class="{ 'active': isActive(item.path) }"
                @click="isMenuOpen = false"
              >
                <div class="mobile-nav-icon">
                  <component :is="item.icon" class="mobile-icon" />
                </div>
                <span class="mobile-nav-text">{{ item.label }}</span>
              </router-link>
            </nav>
          </div>
        </Transition>
      </div>
    </Transition>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { 
  // ChevronRight as ChevronRightIcon, 
  MapPin as MapPinIcon, 
  Calendar as CalendarIcon, 
  Map as MapIcon, 
  Info as InfoIcon,
  Menu as MenuIcon,
  X as XIcon
} from 'lucide-vue-next'
import SeasonSwitcher from './SeasonSwitcher.vue'

const route = useRoute()
const isMenuOpen = ref(false)
const scrolled = ref(false)

// 导航数据
const navItems = [
  { label: '景点推荐', path: '/attractions', icon: MapPinIcon },
  { label: '旅游攻略', path: '/guides', icon: CalendarIcon },
  { label: '地图导航', path: '/map', icon: MapIcon },
  { label: '文化特色', path: '/culture', icon: InfoIcon },
  { label: '关于我们', path: '/aboutus', icon: InfoIcon}
  // 智能规划 这里要更换两个logo
]

// 检查当前路由是否激活
const isActive = (path) => {
  return route.path === path || route.path.startsWith(`${path}/`)
}

// 监听滚动事件，改变导航栏样式
const handleScroll = () => {
  scrolled.value = window.scrollY > 50
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  handleScroll() // 初始检查
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

</script>

<style scoped>
/* 基础样式 */
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.site-header.scrolled {
  padding: 4px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.header-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Logo样式 */
.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-icon {
  width: 32px;
  height: 32px;
  margin-right: 8px;
  border-radius: 4px;
  background: #8B4513;
  position: relative;
  overflow: hidden;
}

.logo-character {
  font-family: 'TitleFont', serif;
  font-size: 18px;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.logo-character.overlay {
  z-index: 2;
}

.logo-hover-effect {
  position: absolute;
  inset: 0;
  background: #C3272B;
  transform-origin: bottom;
  transform: scaleY(0);
  transition: transform 0.3s;
}

.logo-link:hover .logo-hover-effect {
  transform: scaleY(1);
}

.logo-text {
  font-weight: 700;
  font-size: 20px;
  color: #8B4513;
  position: relative;
}

.underline-effect {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: #C3272B;
  transition: width 0.3s;
}

.logo-link:hover .underline-effect {
  width: 100%;
}

/* 导航项样式 */
.desktop-nav {
  display: flex;
  gap: 24px;
}

.nav-item {
  display: flex;
  align-items: center;
  color: rgba(139, 69, 19, 0.8);
  text-decoration: none;
  padding: 8px 0;
  position: relative;
  transition: color 0.2s;
}

.nav-item:hover {
  color: #8B4513;
}

.nav-item.active {
  color: #8B4513;
  font-weight: 500;
}

.nav-icon {
  width: 20px;
  height: 20px;
  margin-right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.nav-item:hover .icon {
  transform: scale(1.1);
}

.nav-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #C3272B;
  transform: scaleX(0);
  transition: transform 0.3s;
}

.nav-item.active .nav-indicator,
.nav-item:hover .nav-indicator {
  transform: scaleX(1);
}

/* 按钮样式 */
.plan-button {
  padding: 6px 16px;
  background: #C3272B;
  color: white;
  border: none;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.button-hover {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, #C3272B, #8B4513);
  opacity: 0;
  transition: opacity 0.3s;
}

.plan-button:hover .button-hover {
  opacity: 1;
}

.button-border {
  position: absolute;
  inset: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.5s;
}

.plan-button:hover .button-border {
  border-color: rgba(245, 222, 179, 0.3);
  transform: scale(1.1);
}

/* 移动端菜单样式 */
.mobile-menu-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 50;
}

.mobile-menu {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 288px;
  background: white;
  padding: 24px;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
}

/* 过渡动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}
.slide-enter-from, .slide-leave-to {
  transform: translateX(100%);
}

/* 响应式设计 */
@media (max-width: 767px) {
  .desktop-nav,
  .season-switcher {
    display: none;
  }
  
  .divider {
    display: none;
  }
}

/* 基础重置 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f5f5f5;
}

::-webkit-scrollbar-thumb {
  background: rgba(139, 69, 19, 0.2);
  border-radius: 3px;
}

.logo-text {
  font-family: system-ui, -apple-system, sans-serif;
}

.site-header {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.admin{
  text-decoration: none; /* 去除下划线 */
  color: inherit; /* 使用与父元素相同的颜色 */
  outline: none; /* 去除点击时形成的红色边框 */
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%; /* 使按钮呈圆形 */
  background-color: #d9b37d; /* 古风的棕黄色 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); /* 阴影效果 */
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.3s ease; /* 过渡效果 */
}

.admin:hover {
      background-color: #c29762; /* 鼠标悬停时的颜色 */
      transform: translateY(-2px); /* 鼠标悬停时上移一点 */
    }

@media (min-width: 768px) {
  .mobile-menu-button {
    display: none;
  }
}
</style>